import { useCallback, useMemo, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import type { UpdateCollaboratorMutation } from 'generated/graphql';
import { UPDATE_BUILD_HUB_COLLABORATOR_API_ROUTE } from '~/routes/remix-api.app.build-hub-collaborator-update';

interface UseBuildHubCollaboratorUpdateOptions {
    onSuccess?: () => void;
}

interface UseBuildHubCollaboratorUpdateReturn {
    updateCollaborator: (formData: FormData) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Hook to handle collaborator update submissions using Remix fetcher
 */
export function useBuildHubCollaboratorUpdate(
    options: UseBuildHubCollaboratorUpdateOptions = {}
): UseBuildHubCollaboratorUpdateReturn {
    const { onSuccess } = options;
    const updateFetcher = useFetcher<UpdateCollaboratorMutation>();
    const data = updateFetcher.data?.buildHubCollaboratorUpdate;
    const isUpdating = updateFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(
        isIdle && data && data.__typename === 'BuildHubCollaborator'
    );

    const updateCollaborator = useCallback(
        (formData: FormData) => {
            updateFetcher.submit(formData, {
                method: 'post',
                action: UPDATE_BUILD_HUB_COLLABORATOR_API_ROUTE,
            });
        },
        [updateFetcher]
    );

    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHubCollaborator') {
            return 'Message' in data
                ? data.Message
                : 'Failed to update collaborator';
        }
        return null;
    }, [isIdle, data]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess, onSuccess]);

    return {
        updateCollaborator,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
